import { NavLink, useNavigate } from "react-router-dom";
import {
  CustomConfirmDialog,
  Typography,
} from "@cointervue/design-system/components";
import { cn } from "@cointervue/design-system";
import {
  leftSidebarBottomItems,
  leftSidebarTopItems,
} from "./leftSideBarItems";
import type { SidebarItemProps } from "@cointervue/shared/types/commonTypes";
import { memo, useState } from "react";
import { useLogoutMutation } from "@/features/auth/authApi";
import { logoutAndReset } from "@/features/auth/authActions";
import { useAppDispatch } from "@/app/store";

const SidebarItem = memo(function SidebarItem({
  to,
  icon,
  label,
  onClick,
  disabled,
}: SidebarItemProps & { disabled?: boolean }) {
  const content = (
    <>
      <img src={icon} alt="leftSidebarIcon" className="w-4 h-4" />
      <Typography as="span" variant="h6" className="text-sm!">
        {label}
      </Typography>
    </>
  );

  if (disabled) {
    return (
      <div className="flex w-full items-center gap-2 px-3 py-2 rounded-md opacity-70 cursor-not-allowed text-brand-black-2">
        {content}
      </div>
    );
  }

  // If no route, render a button (logout case)
  if (!to) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 px-3 py-2 rounded-md transition-colors cursor-pointer text-brand-black-2 hover:bg-brand-light-orange-2"
      >
        {content}
      </button>
    );
  }

  // Normal navigation item
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2 px-3 py-2 rounded-md transition-colors",
          isActive
            ? "bg-brand-light-orange-2 border border-brand-dark-orange"
            : "text-brand-black-2 hover:bg-brand-light-orange-2",
        )
      }
    >
      {content}
    </NavLink>
  );
});

const LeftSideBar = memo(function LeftSideBar() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [logoutApi, { isLoading }] = useLogoutMutation();

  const handleConfirmLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      dispatch(logoutAndReset());
      navigate("/signin", { replace: true });
    }
  };

  return (
    <>
      <div className="w-full h-full bg-white flex flex-col justify-between">
        <div>
          {/* Menu Items */}
          <nav className="mt-4 space-y-2 px-2">
            {leftSidebarTopItems.map((item) => (
              <SidebarItem key={item.to} {...item} />
            ))}
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="mb-4 px-2 space-y-2">
          {leftSidebarBottomItems.map((item) =>
            item.label === "Log out" ? (
              <SidebarItem
                key={item.label}
                {...item}
                onClick={() => setOpenLogoutDialog(true)}
              />
            ) : (
              <SidebarItem key={item.to} {...item} />
            ),
          )}
        </div>
      </div>
      <CustomConfirmDialog
        open={openLogoutDialog}
        onOpenChange={setOpenLogoutDialog}
        title="Log out?"
        description="Are you sure you want to log out of your account?"
        confirmText="Log out"
        cancelText="Cancel"
        destructive
        loading={isLoading}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
});

export default LeftSideBar;
